import { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import companyData from "const/companyData.json";
import { invalidateModuleFinance } from "components/ModulePayments/moduleFinanceQueries";
import { getApiError } from "utils/apiError";
import { formatMoney } from "utils/utils";
import InvoiceLineItemsEditor from "./InvoiceLineItemsEditor";
import { createModuleInvoice, updateModuleInvoice } from "./moduleInvoiceApi";
import {
  companyFor,
  companyHeader,
  formTotals,
  toRequest,
  validateForm,
} from "./moduleInvoiceForm";

const Total = ({ label, value, strong = false }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography variant={strong ? "subtitle2" : "body2"} fontWeight={strong ? 700 : 400}>
      {label}
    </Typography>
    <Typography variant={strong ? "subtitle2" : "body2"} fontWeight={strong ? 700 : 600}>
      {formatMoney(value)}
    </Typography>
  </Stack>
);

const ModuleInvoiceFormBody = ({
  mode,
  moduleType,
  moduleId,
  clientId,
  initialForm,
  onClose,
  onSaved,
}) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(initialForm);
  const totals = useMemo(() => formTotals(form.lines), [form.lines]);
  const setField = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const save = useMutation({
    mutationFn: () =>
      (mode === "create" ? createModuleInvoice : updateModuleInvoice)(
        moduleType,
        moduleId,
        toRequest(form)
      ),
    onSuccess: () => {
      toast.success(mode === "create" ? "Invoice created" : "Invoice updated");
      invalidateModuleFinance(queryClient, moduleType, moduleId, clientId);
      onSaved?.();
      onClose();
    },
    onError: (err) => toast.error(getApiError(err, "Could not save the invoice")),
  });

  const submit = () => {
    const error = validateForm(form);
    if (error) {
      toast.error(error);
      return;
    }
    save.mutate();
  };

  return (
    <>
      <DialogTitle>{mode === "create" ? "Create invoice" : "Edit invoice"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Title"
            size="small"
            required
            value={form.title}
            onChange={setField("title")}
          />
          <Box className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <TextField
              label="Invoice to"
              size="small"
              required
              value={form.invoiceTo}
              onChange={setField("invoiceTo")}
            />
            <TextField
              label="Issue date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.issueDate}
              onChange={setField("issueDate")}
            />
            <TextField
              label="Due date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.dueDate}
              onChange={setField("dueDate")}
            />
          </Box>
          <TextField
            label="Client address"
            size="small"
            value={form.address}
            onChange={setField("address")}
          />
          <Autocomplete
            options={companyData}
            value={companyFor(form.header)}
            getOptionLabel={(company) => company?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, company) =>
              company && setForm((prev) => ({ ...prev, header: companyHeader(company) }))
            }
            renderInput={(params) => <TextField {...params} label="Invoice from" size="small" />}
          />
          <InvoiceLineItemsEditor
            lines={form.lines}
            onChange={(lines) => setForm((prev) => ({ ...prev, lines }))}
          />
          <Box sx={{ ml: "auto", width: { xs: "100%", md: 280 } }}>
            <Total label="Subtotal" value={totals.subtotal} />
            <Total label="GST (10%)" value={totals.gst} />
            <Total label="Total" value={totals.total} strong />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={save.isLoading}>
          {save.isLoading ? "Saving…" : "Save invoice"}
        </Button>
      </DialogActions>
    </>
  );
};

export default ModuleInvoiceFormBody;
